/**
 * Shared canonical ID resolution for Firestore document IDs.
 * Used by dashboardData (reporting reads) and submissions (writes).
 * Aliases per docs/canonical-ids.md — add only when legacy data/code use variants.
 */
import { DEFAULT_COMPLIANCE_CATEGORIES } from './complianceCategoryTypes';

/** Canonical department IDs (from .cursor/rules/nidata/firestore-canonical-ids.mdc). Used for cross-audit validation. */
export const CANONICAL_DEPARTMENT_IDS = new Set([
  'ERD', '20B', '20C', 'OPD_GENP', 'OPD_SxOrthoEENT', 'OPD_MED', 'OPD_OBGYN', 'HD', 'ECC',
  'BLO_BNK', 'OR', 'CSSD', 'G10', 'DR', '18A', '18B', '23A', 'NICU', '18D', '21D', '25A',
  '19B', '19D', '26B', '25B', '18C', '22A', '22D', 'SW', '22B', '22C', '19A', '24A', '24B',
  'MICU', 'CCU', 'SICU', 'BURN', 'STROKE', '19C', '21A', 'OPD_CHEMO', 'NSO',
]);

/**
 * Returns true if the value is a canonical department ID (case-sensitive match).
 */
export function isValidCanonicalDepartmentId(id: string | null | undefined): boolean {
  if (!id || typeof id !== 'string') return false;
  const trimmed = id.trim();
  return trimmed.length > 0 && CANONICAL_DEPARTMENT_IDS.has(trimmed);
}

/**
 * Legacy aliases for compliance category IDs (alias → canonical).
 * When adding new aliases, also update resolveCategoryDocId usage in ComplianceHub.
 */
const COMPLIANCE_CATEGORY_ALIASES: Record<string, string> = {
  nurse_note: 'nurse_notes',
  'nurse-notes': 'nurse_notes',
  /** Thai title for nurse_notes — used as doc ID in department-path submissions */
  'บันทึกทางการพยาบาล': 'nurse_notes',
  'บรรยากาศจริยธรรมในองค์การพยาบาล': 'orgClimateSurveys',
  'แบบประเมินบรรยากาศจริยธรรมในองค์การพยาบาล': 'orgClimateSurveys',
  /** Mistyped product name; canonical submission category is med_error (นิเทศระบบยา) */
  medsystem: 'med_error',
};

/**
 * Resolve to canonical Firestore /complianceCategories doc ID.
 * Supports legacy aliases (e.g. nurse_note → nurse_notes per docs/canonical-ids.md).
 */
export function resolveCanonicalCategoryId(categoryId?: string): string | null {
  if (!categoryId) return null;
  const normalized = categoryId.trim();
  if (!normalized) return null;
  const alias = COMPLIANCE_CATEGORY_ALIASES[normalized];
  if (alias) return alias;
  const match = DEFAULT_COMPLIANCE_CATEGORIES.find(
    (cat) => cat.id === normalized || cat.title === normalized,
  );
  return match?.id ?? normalized;
}

/**
 * Firestore doc IDs under `submissions/{dept}/{month}/{docId}` may use canonical id, Thai title,
 * or legacy aliases. Used for targeted getDoc reads so we do not list the whole month collection
 * (listing fails security rules if the user cannot read every category doc in that folder).
 */
export function submissionCategoryDocIdsToTry(resolvedCategoryId: string): string[] {
  const trimmed = resolvedCategoryId.trim();
  if (!trimmed) return [];

  const canonical = resolveCanonicalCategoryId(trimmed) ?? trimmed;
  const ids = new Set<string>();
  ids.add(trimmed);
  ids.add(canonical);

  for (const [aliasKey, target] of Object.entries(COMPLIANCE_CATEGORY_ALIASES)) {
    if (target === canonical) ids.add(aliasKey);
  }

  for (const cat of DEFAULT_COMPLIANCE_CATEGORIES) {
    if (cat.id === canonical) {
      ids.add(cat.id);
      ids.add(cat.title);
    }
  }

  return Array.from(ids).filter((id) => id.length > 0 && !id.includes('/'));
}

/** Returns true if the category ID matches a known compliance category. */
export function isValidCanonicalCategoryId(categoryId?: string | null): boolean {
  const resolved = resolveCanonicalCategoryId(categoryId);
  if (!resolved) return false;
  return DEFAULT_COMPLIANCE_CATEGORIES.some((c) => c.id === resolved);
}
